import { SERVER_URL, getHeaders, handleResponse } from '../apiConfig';

const API_BASE_URL = `${SERVER_URL}/api/rtmp-sessions`;

// Fetch RTMP sessions by module ID
export const getSessionsByModule = async (moduleId, queryParams = {}) => {
  const params = new URLSearchParams();
  if (moduleId) params.append('module_id', moduleId);
  if (queryParams.status) params.append('status', queryParams.status);
  if (queryParams.search) params.append('search', queryParams.search);

  const url = `${API_BASE_URL}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Schedule a new RTMP session
export const scheduleRTMPSession = async (sessionData) => {
  const response = await fetch(API_BASE_URL, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(sessionData)
  });
  return handleResponse(response);
};

// Update an existing RTMP session
export const updateRTMPSession = async (sessionId, sessionData) => {
  const response = await fetch(`${API_BASE_URL}/${sessionId}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(sessionData)
  });
  return handleResponse(response);
};

// Delete an RTMP session
export const deleteRTMPSession = async (sessionId) => {
  const response = await fetch(`${API_BASE_URL}/${sessionId}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Update RTMP session status (SCHEDULED, LIVE, COMPLETED, CANCELLED)
export const updateRTMPSessionStatus = async (sessionId, status) => {
  const response = await fetch(`${API_BASE_URL}/${sessionId}/status`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ rtmp_status: status, status })
  });
  return handleResponse(response);
};

// Aliases for compatibility
export const startRTMPSession = async (sessionId) => {
  return updateRTMPSessionStatus(sessionId, 'LIVE');
};

export const stopRTMPSession = async (sessionId) => {
  return updateRTMPSessionStatus(sessionId, 'COMPLETED');
};

export const cancelRTMPSession = async (sessionId) => {
  return updateRTMPSessionStatus(sessionId, 'CANCELLED');
};

// Stream WebM chunk from browser to RTMP server & recorder
export const sendWebBroadcastChunk = async (sessionId, blob, { videoBitrate = '1000k', audioBitrate = '128k', isFirstChunk = false } = {}) => {
  const token = localStorage.getItem('token');
  const params = new URLSearchParams({
    videoBitrate,
    audioBitrate,
    ...(isFirstChunk ? { first: '1' } : {})
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 12000);
  try {
    const response = await fetch(`${API_BASE_URL}/${sessionId}/web-stream?${params.toString()}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'video/webm',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: blob,
      signal: controller.signal
    });
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
};

// Stop active Web Broadcast FFmpeg publisher
export const stopWebBroadcast = async (sessionId) => {
  const response = await fetch(`${API_BASE_URL}/${sessionId}/web-stream/stop`, {
    method: 'POST',
    headers: getHeaders()
  });
  return handleResponse(response);
};

// Fetch recorded local file (.mkv or .flv) for saving to local system disk
export const fetchWebBroadcastLocalFile = async (sessionId, format = 'mkv') => {
  const token = localStorage.getItem('token');
  const fmt = String(format || 'mkv').toLowerCase() === 'flv' ? 'flv' : 'mkv';
  const response = await fetch(`${API_BASE_URL}/${sessionId}/web-stream/local-file?format=${fmt}`, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
  if (!response.ok) {
    throw new Error(`Unable to fetch local ${fmt.toUpperCase()} recording (${response.status})`);
  }
  return response.blob();
};

